import { BoxOfferDropInIcon } from "@/assets/images/svgIcons/boxOffer/dropInIcon"
import { BoxOfferPassIcon } from "@/assets/images/svgIcons/boxOffer/passIcon"
import { BoxOfferPlanIcon } from "@/assets/images/svgIcons/boxOffer/planIcon"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { KPI_TOOLTIP_DELAY } from "@/modules/boxDashboard/modules/KPI/components/kpiBlock"
import { VariationBadge } from "@/modules/boxDashboard/modules/KPI/components/variationBadge"
import type {
  ActivationCountModel,
  ActivationsKpiModel,
} from "@/modules/boxDashboard/modules/KPI/modules/activations/models/activationsKpiModel"
import { word } from "@modules/core/utils/i18n"

type ActivationsKpiBlockProps = {
  model: ActivationsKpiModel
}

export function ActivationsKpiBlock({ model }: ActivationsKpiBlockProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Text fontStyle="TM" className="font-semibold text-textOnSurface-default">
          {word("boxDashboard.kpi.recentActivations.title")}
        </Text>
      </div>

      <div className="flex gap-4">
        <ActivationCard
          icon={<BoxOfferPlanIcon size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.recentActivations.membership.title")}
          tooltip={word("boxDashboard.kpi.recentActivations.membership.tooltip")}
          activation={model.membership}
        />
        <ActivationCard
          icon={<BoxOfferPassIcon size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.recentActivations.classPass.title")}
          tooltip={word("boxDashboard.kpi.recentActivations.classPass.tooltip")}
          activation={model.classPass}
        />
        <ActivationCard
          icon={<BoxOfferDropInIcon size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.recentActivations.dropIn.title")}
          tooltip={word("boxDashboard.kpi.recentActivations.dropIn.tooltip")}
          activation={model.dropIn}
        />
      </div>
    </div>
  )
}

type ActivationCardProps = {
  icon: React.ReactNode
  title: string
  tooltip: string
  activation: ActivationCountModel
}

function ActivationCard({ icon, title, tooltip, activation }: ActivationCardProps) {
  return (
    <TooltipProvider delay={KPI_TOOLTIP_DELAY}>
      <Tooltip>
        <TooltipTrigger
          className="cursor-help"
          render={
            <div className="flex flex-1 flex-col gap-2 rounded-xl bg-surface-background p-4">
              <div className="flex items-center gap-2">{icon}</div>

              <div className="flex flex-col gap-1">
                <Text fontStyle="LM" className="font-semibold text-textOnSurface-muted">
                  {title}
                </Text>

                <div className="flex items-center gap-2">
                  <Text fontStyle="HM" className="text-textOnSurface-default">
                    {activation.count === 0 && [0, null].includes(activation.comparisonCount) ? "–" : activation.count}
                  </Text>
                  {activation.comparisonCount !== null && activation.variationDirection !== null && (
                    <VariationBadge
                      value={activation.comparisonCount}
                      direction={activation.variationDirection}
                      type="points"
                    />
                  )}
                </div>
              </div>
            </div>
          }
        />
        <TooltipContent className="max-w-80">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
            {tooltip}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
